import { useState, useCallback, useMemo } from 'react';

/**
 * Sélection multiple pour les listes (clients, actifs, utilisateurs, alertes).
 *
 * La sélection est TOUJOURS une liste d'IDs explicites : on n'envoie jamais un filtre
 * au serveur, donc on n'agit jamais sur ce que l'utilisateur n'a pas vu à l'écran.
 * `toggleAll` ne coche que les lignes actuellement chargées — les listes sont
 * plafonnées côté API, et le compteur affiché le dit.
 *
 * @param {Array<{id: string}>} items lignes actuellement affichées
 */
export default function useBulkSelection(items = []) {
  const [selected, setSelected] = useState(new Set());

  const visibleIds = useMemo(() => items.map(i => i.id), [items]);

  const toggle = useCallback((id) => {
    setSelected(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  }, []);

  const toggleAll = useCallback(() => {
    setSelected(prev => (
      visibleIds.length > 0 && visibleIds.every(id => prev.has(id))
        ? new Set()
        : new Set(visibleIds)
    ));
  }, [visibleIds]);

  const clear = useCallback(() => setSelected(new Set()), []);

  const isSelected = useCallback((id) => selected.has(id), [selected]);

  // Les éléments cochés qui sont encore affichés — après un changement de filtre,
  // une sélection résiduelle ne doit pas partir dans une action de masse.
  const selectedItems = useMemo(
    () => items.filter(i => selected.has(i.id)),
    [items, selected],
  );

  const ids = useMemo(() => selectedItems.map(i => i.id), [selectedItems]);

  return {
    selected,
    ids,
    selectedItems,
    count: ids.length,
    allSelected: visibleIds.length > 0 && visibleIds.every(id => selected.has(id)),
    toggle,
    toggleAll,
    clear,
    isSelected,
  };
}
